<template>
  <div class="avatar-pointer-container">
    <div
      class="hand-wrapper"
      :style="{
        '--img-height': `${handSize.height}`,
        '--final-left': `${props.promptLocation}px`,
        '--animation-duration': `${animationDuration / 2}s`,
        '--start-position': `${startPosition}px`
      }"
    >
      <img
        id="avatar-hand-img"
        ref="handRef"
        class="avatar-hand"
        src="@/assets/images/SprocketFoamFinger.png"
        :class="{
          'move-to-left': isAnimating && !isMovingToFinal && !moveRight,
          'move-to-right': isAnimating && !isMovingToFinal && moveRight,
          'move-to-final-location': isMovingToFinal
        }"
        @animationend="onAnimationEnd"
        @load="onImageLoad"
      />
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, watch, nextTick } from 'vue'

const props = defineProps({
  waitTime: {
    type: Number,
    default: 0
  },
  promptLocation: {
    type: Number,
    default: 0
  },
  moveToLocation: {
    type: Boolean,
    default: false
  }
})

const emit = defineEmits<{
  (e: 'answer-selected'): void
}>()

const handRef = ref<HTMLImageElement | null>(null)
const handSize = ref({ height: 'auto', width: 'auto' })
const isAnimating = ref(false)
const isMovingToFinal = ref(false)
const moveRight = ref(false)
const startPosition = ref(0)

const animationDuration = ref(props.waitTime)

const onImageLoad = () => {
  if (handRef.value) {
    handSize.value.height = `${handRef.value.naturalHeight}px`
    handSize.value.width = `${handRef.value.naturalWidth}px`
    startHorizontalAnimation()
  }
}

const startHorizontalAnimation = () => {
  isAnimating.value = true
  moveRight.value = false
}

const onAnimationEnd = (event: AnimationEvent) => {
  if (event.animationName.includes('moveToLeft') || event.animationName.includes('moveToRight')) {
    const computedStyle = window.getComputedStyle(event.target as Element)
    const matrix = new DOMMatrixReadOnly(computedStyle.transform)
    startPosition.value = matrix.m41

    if (!props.moveToLocation) {
      moveRight.value = !moveRight.value
      nextTick(() => {
        isAnimating.value = true
      })
    } else {
      isAnimating.value = false
      isMovingToFinal.value = true
    }
  } else if (event.animationName.includes('moveToFinal')) {
    emit('answer-selected')
  }
}

watch(
  () => props.moveToLocation,
  (newValue) => {
    if (newValue && !isAnimating.value) {
      if (handRef.value) {
        const computedStyle = window.getComputedStyle(handRef?.value)
        const matrix = new DOMMatrixReadOnly(computedStyle.transform)
        startPosition.value = matrix.m41
      }
      isMovingToFinal.value = true
    }
  }
)
</script>

<style scoped lang="scss">
.hand-wrapper {
  --x-right-position: calc((var(--full-vw) - 100%) - 8rem);
  --x-final-location: calc((var(--final-left) - 8rem) - 20%);
  --y-hand-lowpoint: calc((var(--full-vh) * 0.07 + 2rem) * -1);
  --y-hand-highpoint: calc((var(--full-vh) * 0.07) * -1);
  position: absolute;
  top: var(--y-hand-lowpoint);
  left: 0;
  z-index: 1000;
  height: calc(var(--full-vh) * 0.3);
  max-height: var(--img-height);
  will-change: transform;
  animation: verticalMove 3s ease-in-out infinite;

  .avatar-pointer-container {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
  }

  .avatar-hand {
    height: calc(var(--full-vh) * 0.3);
    max-height: var(--img-height);
  }

  .move-to-left {
    @include animation(moveToLeft var(--animation-duration) ease-in-out forwards);
  }

  .move-to-right {
    @include animation(moveToRight var(--animation-duration) ease-in-out forwards);
  }

  .move-to-final-location {
    @include animation(moveToFinal 1s ease-in-out forwards);
  }
}

@include keyframes(moveToLeft) {
  0% {
    @include prefixed(transform, translateX(var(--x-right-position)));
    @include prefixed(transform, translate3d(var(--x-right-position), 0, 0));
  }
  100% {
    @include prefixed(transform, translateX(0));
    @include prefixed(transform, translate3d(0, 0, 0));
  }
}

@include keyframes(moveToRight) {
  0% {
    @include prefixed(transform, translateX(0));
    @include prefixed(transform, translate3d(0, 0, 0));
  }
  100% {
    @include prefixed(transform, translateX(var(--x-right-position)));
    @include prefixed(transform, translate3d(var(--x-right-position), 0, 0));
  }
}

@include keyframes(verticalMove) {
  0%,
  100% {
    top: var(--y-hand-lowpoint);
  }
  50% {
    top: var(--y-hand-highpoint);
  }
}

@include keyframes(moveToFinal) {
  0% {
    @include prefixed(transform, translateX(var(--start-position)));
    @include prefixed(transform, translate3d(var(--start-position), 0, 0));
  }
  100% {
    @include prefixed(transform, translateX(var(--x-final-location)));
    @include prefixed(transform, translate3d(var(--x-final-location), 0, 0));
  }
}
</style>
